import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

type CurrentLine = {
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  segments: number;
  baseY: number;
  amplitude: number;
  frequency: number;
  phase: number;
  depth: number;
  speed: number;
};

type Ripple = {
  line: THREE.Line;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  points: number;
  baseRadius: number;
  phase: number;
  speed: number;
  centerX: number;
  centerY: number;
  wobble: number;
  drift: number;
  depth: number;
};

const ThreeBackground: React.FC = () => {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mountElement = mountRef.current;
    if (!mountElement) return;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x090b10, 0.0035);

    const camera = new THREE.PerspectiveCamera(58, window.innerWidth / window.innerHeight, 0.1, 220);
    camera.position.z = 44;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mountElement.appendChild(renderer.domElement);

    const textureCanvas = document.createElement('canvas');
    textureCanvas.width = 64;
    textureCanvas.height = 64;
    const textureContext = textureCanvas.getContext('2d');

    if (textureContext) {
      const gradient = textureContext.createRadialGradient(32, 32, 2, 32, 32, 30);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
      gradient.addColorStop(0.36, 'rgba(220, 228, 238, 0.55)');
      gradient.addColorStop(1, 'rgba(220, 228, 238, 0)');
      textureContext.fillStyle = gradient;
      textureContext.fillRect(0, 0, 64, 64);
    }

    const dustTexture = new THREE.CanvasTexture(textureCanvas);

    const dustPalette = [
      new THREE.Color(0xf2f5fa),
      new THREE.Color(0xd4dbe5),
      new THREE.Color(0xb7c0cc),
      new THREE.Color(0x8f99a8),
    ];

    const createDustLayer = (count: number, size: number, opacity: number, spread: number) => {
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(count * 3);
      const colors = new Float32Array(count * 3);

      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        positions[i3] = (Math.random() - 0.5) * spread;
        positions[i3 + 1] = (Math.random() - 0.5) * spread * 0.76;
        positions[i3 + 2] = (Math.random() - 0.5) * spread * 0.92;

        const tone = dustPalette[Math.floor(Math.random() * dustPalette.length)].clone();
        tone.offsetHSL((Math.random() - 0.5) * 0.01, (Math.random() - 0.5) * 0.02, (Math.random() - 0.5) * 0.06);

        colors[i3] = tone.r;
        colors[i3 + 1] = tone.g;
        colors[i3 + 2] = tone.b;
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

      const material = new THREE.PointsMaterial({
        size,
        map: dustTexture,
        transparent: true,
        opacity,
        depthWrite: false,
        vertexColors: true,
        blending: THREE.NormalBlending,
      });

      const points = new THREE.Points(geometry, material);
      scene.add(points);

      return { points, geometry, material, count };
    };

    const nearDust = createDustLayer(820, 0.23, 0.42, 148);
    const farDust = createDustLayer(1180, 0.15, 0.28, 186);

    const rippleGroup = new THREE.Group();
    scene.add(rippleGroup);
    const ripples: Ripple[] = [];

    for (let i = 0; i < 5; i++) {
      const points = 170;
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(points * 3);
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const material = new THREE.LineBasicMaterial({
        color: i % 2 === 0 ? 0xcfd6e1 : 0xaeb8c5,
        transparent: true,
        opacity: 0.11 + i * 0.018,
      });

      const line = new THREE.Line(geometry, material);
      rippleGroup.add(line);

      ripples.push({
        line,
        geometry,
        positions,
        points,
        baseRadius: 8.5 + i * 5.6,
        phase: Math.random() * Math.PI * 2,
        speed: 0.22 + i * 0.045,
        centerX: (Math.random() - 0.5) * 8,
        centerY: (Math.random() - 0.5) * 5,
        wobble: 0.24 + i * 0.05,
        drift: 1.6 + i * 0.35,
        depth: -24 + i * 2.8,
      });
    }

    const currents: CurrentLine[] = [];
    const currentGroup = new THREE.Group();
    scene.add(currentGroup);

    const currentConfigs = [
      { color: 0xd5dce6, baseY: -7.2, amp: 2.7, freq: 0.059, phase: 0.0, depth: -14, speed: 0.28, opacity: 0.22 },
      { color: 0xb9c2cf, baseY: 5.3, amp: 2.1, freq: 0.066, phase: 1.9, depth: -18, speed: 0.35, opacity: 0.2 },
      { color: 0x9da8b7, baseY: -0.8, amp: 1.5, freq: 0.078, phase: 3.2, depth: -22, speed: 0.24, opacity: 0.17 },
    ];

    currentConfigs.forEach((config) => {
      const segments = 220;
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(segments * 3);
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const material = new THREE.LineBasicMaterial({
        color: config.color,
        transparent: true,
        opacity: config.opacity,
      });

      const line = new THREE.Line(geometry, material);
      currentGroup.add(line);

      currents.push({
        geometry,
        positions,
        segments,
        baseY: config.baseY,
        amplitude: config.amp,
        frequency: config.freq,
        phase: config.phase,
        depth: config.depth,
        speed: config.speed,
      });
    });

    const glowGeometry = new THREE.SphereGeometry(1.3, 24, 24);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xe8edf5,
      transparent: true,
      opacity: 0.09,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const glowOrbs: THREE.Mesh[] = [];
    const glowBases: Array<{ x: number; y: number; z: number }> = [];

    for (let i = 0; i < 3; i++) {
      const orb = new THREE.Mesh(glowGeometry, glowMaterial.clone());
      const base = {
        x: (Math.random() - 0.5) * 54,
        y: (Math.random() - 0.5) * 24,
        z: -10 - Math.random() * 18,
      };
      orb.position.set(base.x, base.y, base.z);
      const scale = 2.4 + Math.random() * 1.8;
      orb.scale.set(scale, scale, scale);
      scene.add(orb);
      glowOrbs.push(orb);
      glowBases.push(base);
    }

    let mouseX = 0;
    let mouseY = 0;

    const onMouseMove = (event: MouseEvent) => {
      mouseX = (event.clientX / window.innerWidth - 0.5) * 2;
      mouseY = (event.clientY / window.innerHeight - 0.5) * 2;
    };

    window.addEventListener('mousemove', onMouseMove);

    const clock = new THREE.Clock();
    let frameId = 0;

    const animateDust = (
      layer: { points: THREE.Points; geometry: THREE.BufferGeometry; count: number },
      speed: number
    ) => {
      const positions = layer.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < layer.count; i++) {
        const i3 = i * 3;
        positions[i3 + 1] += speed;
        if (positions[i3 + 1] > 58) {
          positions[i3 + 1] = -58;
        }
      }
      layer.geometry.attributes.position.needsUpdate = true;
    };

    const animate = () => {
      const t = clock.getElapsedTime();

      animateDust(nearDust, 0.010);
      animateDust(farDust, 0.007);

      nearDust.points.rotation.y = Math.sin(t * 0.09) * 0.035;
      nearDust.points.rotation.x = Math.cos(t * 0.06) * 0.025;
      farDust.points.rotation.y = Math.cos(t * 0.08) * -0.03;

      ripples.forEach((ripple, index) => {
        for (let i = 0; i < ripple.points; i++) {
          const i3 = i * 3;
          const pct = i / (ripple.points - 1);
          const theta = pct * Math.PI * 2;
          const pulse = Math.sin(t * ripple.speed + ripple.phase) * 1.2;
          const radius =
            ripple.baseRadius +
            pulse +
            Math.sin(theta * 4 + t * 0.65 + ripple.phase) * ripple.wobble;

          ripple.positions[i3] =
            ripple.centerX +
            Math.cos(theta) * radius +
            Math.sin(t * 0.13 + ripple.phase) * ripple.drift;
          ripple.positions[i3 + 1] =
            ripple.centerY +
            Math.sin(theta) * radius * 0.62 +
            Math.cos(t * 0.17 + ripple.phase) * ripple.drift * 0.55;
          ripple.positions[i3 + 2] =
            ripple.depth +
            Math.sin(theta * 3 + t * 0.4 + index) * 0.26;
        }

        ripple.geometry.attributes.position.needsUpdate = true;
        ripple.line.rotation.z = Math.sin(t * 0.08 + index) * 0.02;
      });

      currents.forEach((current) => {
        for (let i = 0; i < current.segments; i++) {
          const i3 = i * 3;
          const pct = i / (current.segments - 1);
          const x = (pct - 0.5) * 172;
          const waveA =
            Math.sin(x * current.frequency + t * current.speed + current.phase) *
            current.amplitude;
          const waveB =
            Math.cos(
              x * (current.frequency * 0.58) -
                t * current.speed * 0.7 +
                current.phase
            ) *
            current.amplitude *
            0.4;

          current.positions[i3] = x;
          current.positions[i3 + 1] = current.baseY + waveA + waveB;
          current.positions[i3 + 2] =
            current.depth +
            Math.sin(x * 0.03 + t * 0.36 + current.phase) * 2.1;
        }

        current.geometry.attributes.position.needsUpdate = true;
      });

      glowOrbs.forEach((orb, index) => {
        const base = glowBases[index];
        orb.position.x = base.x + Math.cos(t * 0.22 + index * 0.7) * 1.25;
        orb.position.y = base.y + Math.sin(t * 0.31 + index) * 1.15;
        orb.position.z = base.z + Math.sin(t * 0.19 + index * 0.5) * 0.7;
      });

      rippleGroup.rotation.z = Math.sin(t * 0.05) * 0.018;
      currentGroup.rotation.y = Math.cos(t * 0.05) * 0.04;

      camera.position.x += (mouseX * 2.8 - camera.position.x) * 0.04;
      camera.position.y += (-mouseY * 1.8 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', onMouseMove);

      nearDust.geometry.dispose();
      nearDust.material.dispose();
      farDust.geometry.dispose();
      farDust.material.dispose();

      ripples.forEach((ripple) => {
        ripple.geometry.dispose();
        (ripple.line.material as THREE.Material).dispose();
      });

      currents.forEach((current, index) => {
        current.geometry.dispose();
        const line = currentGroup.children[index] as THREE.Line;
        (line.material as THREE.Material).dispose();
      });

      glowGeometry.dispose();
      glowOrbs.forEach((orb) => {
        (orb.material as THREE.Material).dispose();
      });

      dustTexture.dispose();
      renderer.dispose();

      if (mountElement.contains(renderer.domElement)) {
        mountElement.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default ThreeBackground;
